import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Typography, Button } from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import '../../index.css';

interface Lookup {
  _id: string;
  category: string;
  value: string;
  displayName: string;
  active: boolean;
  sessionRate?: number;
  numberOfSessions?: number;
}

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  organisation?: string;
  organisationDisplayName?: string;
  referralCategory: 'direct' | 'organisation';
  referralType: string;
  referralTypeDisplayName?: string;
  phone?: string;
  assessed: boolean;
  assessmentDateTime?: string;
  assessmentStatus?: 'not_started' | 'in_progress' | 'rescheduled';
  rescheduleNotes?: string;
}

function AssessorPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [referralTypes, setReferralTypes] = useState<Lookup[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const clientsPerPage = 10;
  const navigate = useNavigate();
  // Get the base URL without duplicating /api
  const API_BASE_URL = (process.env.REACT_APP_API_URL || '').endsWith('/api') 
    ? (process.env.REACT_APP_API_URL || '').slice(0, -4) // Remove trailing /api
    : (process.env.REACT_APP_API_URL || '');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    // Construct URL properly to avoid double /api
    const clientsUrl = API_BASE_URL.endsWith('/api') 
      ? `${API_BASE_URL}/clients?assessed=false`
      : `${API_BASE_URL}/api/clients?assessed=false`;
      
    axios
      .get(clientsUrl, config)
      .then(response => {
        const unassessedClients: Client[] = response.data
          .filter((client: Client) => 
            !client.assessed || 
            client.assessmentStatus === 'in_progress' || 
            client.assessmentStatus === 'rescheduled'
          )
          .sort((a: Client, b: Client) => {
            if (a.assessmentDateTime && b.assessmentDateTime) {
              return new Date(a.assessmentDateTime).getTime() - new Date(b.assessmentDateTime).getTime();
            }
            if (a.assessmentDateTime) return -1;
            if (b.assessmentDateTime) return 1;
            return 0;
          });
        setClients(unassessedClients || []);
        setFilteredClients(unassessedClients || []);
      })
      .catch(error => {
        console.error('Error fetching clients:', error);
        setClients([]);
        setFilteredClients([]);
      });

    // Construct URL properly to avoid double /api
    const referralTypesUrl = API_BASE_URL.endsWith('/api') 
      ? `${API_BASE_URL}/lookups/admin/referralTypes`
      : `${API_BASE_URL}/api/lookups/admin/referralTypes`;
      
    axios.get(referralTypesUrl, config)
      .then((response) => {
        console.log('Referral types:', response.data);
        setReferralTypes(response.data || []);
      })
      .catch(error => {
        console.error('Error fetching referral types:', error);
        setReferralTypes([]);
      });
  }, [API_BASE_URL]);

  const getReferralTypeDisplayName = (referralType: string) => {
    const lookup = referralTypes.find(rt => rt.value === referralType);
    console.log('Looking up referral type:', referralType, 'Found:', lookup);
    return lookup?.displayName || referralType;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = clients.filter(client =>
      `${client.firstName} ${client.lastName}`.toLowerCase().includes(query) ||
      (client.organisation && client.organisation.toLowerCase().includes(query))
    );
    setFilteredClients(filtered);
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * clientsPerPage;
  const indexOfFirstItem = indexOfLastItem - clientsPerPage;
  const totalItems = filteredClients.length;
  const totalPages = Math.ceil(totalItems / clientsPerPage);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>

                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Assessor Page
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="max-w-7xl mx-auto py-8">
            {/* Search Section */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by name or organisation..."
                value={searchQuery}
                onChange={handleSearch}
                className="theme-input"
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto glass-card rounded-lg">
              <table className="min-w-full">
                <thead className="glass-card-light">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Referral Source</th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Assessment Date/Time</th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Assessment Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">Reschedule Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredClients.length > 0 ? (
                    filteredClients.slice(indexOfFirstItem, indexOfLastItem).map((client) => (
                      <tr key={client._id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium theme-text-primary">
                            <Link 
                              to={`/client-details/${client._id}`}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              {client.firstName} {client.lastName}
                            </Link>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm theme-text-secondary">
                          {client.referralCategory === 'organisation'
                            ? (client.organisationDisplayName || client.organisation || 'N/A')
                            : (client.referralTypeDisplayName || getReferralTypeDisplayName(client.referralType) || 'N/A')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm theme-text-secondary">
                          {client.phone || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm theme-text-secondary">
                          {client.assessmentDateTime 
                            ? new Date(client.assessmentDateTime).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })
                            : 'Not scheduled'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(!client.assessmentStatus || client.assessmentStatus === 'not_started') && (
                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#2196f3',
                                '&:hover': {
                                  backgroundColor: '#1976d2'
                                }
                              }}
                              onClick={() => navigate(`/assessment-part-1/${client._id}`)}
                            >
                              Start Assessment
                            </Button>
                          )}
                          {client.assessmentStatus === 'in_progress' && (
                            <div className="flex items-center">
                              <span className="text-yellow-600 font-medium mr-2">In Progress</span>
                              <Button
                                variant="contained"
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  backgroundColor: '#ff9800',
                                  '&:hover': {
                                    backgroundColor: '#f57c00'
                                  }
                                }}
                                onClick={() => navigate(`/assessment-part-1/${client._id}`)}
                              >
                                Continue
                              </Button>
                            </div>
                          )}
                          {client.assessmentStatus === 'rescheduled' && (
                            <div className="flex items-center">
                              <span className="text-orange-600 font-medium mr-2">Rescheduled</span>
                              <Button
                                variant="contained"
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  backgroundColor: '#ed6c02',
                                  '&:hover': {
                                    backgroundColor: '#e65100'
                                  }
                                }}
                                onClick={() => navigate(`/assessment-part-1/${client._id}`)}
                              >
                                Start
                              </Button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm theme-text-secondary">
                          {client.rescheduleNotes || 'N/A'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center theme-text-secondary">
                        No clients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-white/5 theme-text-muted'
                      : 'glass-card theme-text-primary hover:bg-white/10'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-white/5 theme-text-muted'
                      : 'glass-card theme-text-primary hover:bg-white/10'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm theme-text-primary">
                    Showing{' '}
                    <span className="font-medium">{indexOfFirstItem + 1}</span>
                    {' - '}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/20 glass-card text-sm font-medium ${
                        currentPage === 1
                          ? 'theme-text-muted'
                          : 'theme-text-primary hover:bg-white/10'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/20 glass-card text-sm font-medium ${
                        currentPage === totalPages
                          ? 'theme-text-muted'
                          : 'theme-text-primary hover:bg-white/10'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </ThemeBackground>
  );
}

export default AssessorPage;